import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Users, Zap, ArrowRight, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_5309bd56-751f-4453-bda3-7be8ecfcb781/artifacts/orcvyuj1_let%20see%20whos%20on.png";

const AVATARS = [
  { seed: "WhosonAlex2025",   bg: "from-violet-500 to-purple-700",   name: "Alex"   },
  { seed: "WhosonJordan2025", bg: "from-cyan-500 to-blue-700",      name: "Jordan" },
  { seed: "WhosonRiley2025",  bg: "from-rose-400 to-pink-700",      name: "Riley"  },
  { seed: "WhosonMorgan2025", bg: "from-amber-400 to-orange-600",   name: "Morgan" },
  { seed: "WhosonCasey2025",  bg: "from-emerald-400 to-teal-700",   name: "Casey"  },
  { seed: "WhosonSam2025",    bg: "from-fuchsia-500 to-purple-700", name: "Sam"    },
];

// ── Live schedule banner ───────────────────────────────────────────────────────
function ScheduleBanner() {
  const [state, setState] = useState(null);

  useEffect(() => {
    const pad = n => String(n).padStart(2, "0");

    const compute = () => {
      const now  = new Date();
      // UK time components via Intl — handles BST/GMT automatically
      const ukParts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/London",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
      }).formatToParts(now);

      const ukH = parseInt(ukParts.find(p => p.type === "hour")?.value   ?? "0");
      const ukM = parseInt(ukParts.find(p => p.type === "minute")?.value ?? "0");
      const ukS = parseInt(ukParts.find(p => p.type === "second")?.value ?? "0");

      const totalSec = ukH * 3600 + ukM * 60 + ukS;
      const OPEN  = 19 * 3600;  // 7 PM
      const CLOSE = 23 * 3600;  // 11 PM
      const isOpen = totalSec >= OPEN && totalSec < CLOSE;

      // Seconds to next transition
      let diff;
      if (isOpen) {
        diff = CLOSE - totalSec;
      } else if (totalSec < OPEN) {
        diff = OPEN - totalSec;       // opens later today
      } else {
        diff = (86400 - totalSec) + OPEN;  // after 11 PM → tomorrow 7 PM
      }

      const dh = Math.floor(diff / 3600);
      const dm = Math.floor((diff % 3600) / 60);
      const ds = diff % 60;

      const countdown = dh > 0
        ? `${dh}h ${pad(dm)}m ${pad(ds)}s`
        : `${pad(dm)}m ${pad(ds)}s`;

      const isClosingSoon = isOpen && diff <= 15 * 60;    // < 15 min left
      const isOpeningSoon = !isOpen && diff <= 30 * 60;  // < 30 min to open
      const when = (totalSec >= CLOSE) ? "tomorrow" : "tonight";

      setState({ isOpen, countdown, isClosingSoon, isOpeningSoon, when, ukH, ukM });
    };

    compute();
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, []);

  if (!state) return null;
  const { isOpen, countdown, isClosingSoon, isOpeningSoon, when } = state;

  /* ── OPEN state ───────────────────────────────────────────────────── */
  if (isOpen) {
    return (
      <motion.div
        data-testid="schedule-banner-open"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`w-full max-w-md mx-auto rounded-2xl border px-5 py-4 text-center mb-8 ${
          isClosingSoon
            ? "bg-amber-500/8 border-amber-500/30"
            : "bg-emerald-500/6 border-emerald-500/25"
        }`}
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className={`w-2 h-2 rounded-full ${isClosingSoon ? "bg-amber-400 animate-pulse" : "bg-emerald-400 pulse-online"}`} />
          <span className={`text-xs font-mono uppercase tracking-widest font-bold ${isClosingSoon ? "text-amber-400" : "text-emerald-400"}`}>
            {isClosingSoon ? "Closing soon" : "Live now"}
          </span>
        </div>
        <p className="text-white font-semibold text-base mb-1">
          {isClosingSoon
            ? "Who's On? closes at 11pm tonight"
            : "Who's On? is open — rooms are live"}
        </p>
        <p className="text-slate-400 text-xs mb-3">
          {isClosingSoon
            ? "Tonight's chat closes and is cleared at 11pm UK time."
            : "Public chatrooms are open until 11pm UK time."}
        </p>
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-mono text-xl font-bold tracking-tight ${
          isClosingSoon ? "text-amber-300 bg-amber-500/10" : "text-emerald-300 bg-emerald-500/10"
        }`}>
          <Clock className="w-4 h-4 shrink-0" />
          {countdown}
          <span className="text-xs font-sans font-normal text-slate-500 ml-1">left</span>
        </div>
      </motion.div>
    );
  }

  /* ── CLOSED state ─────────────────────────────────────────────────── */
  return (
    <motion.div
      data-testid="schedule-banner-closed"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`w-full max-w-md mx-auto rounded-2xl border px-5 py-4 text-center mb-8 ${
        isOpeningSoon
          ? "bg-violet-500/8 border-violet-500/40"
          : "bg-[#0E0E17] border-purple-500/20"
      }`}
    >
      {/* Label */}
      <div className="flex items-center justify-center gap-2 mb-2">
        <Clock className={`w-3.5 h-3.5 ${isOpeningSoon ? "text-violet-400" : "text-slate-500"}`} />
        <span className={`text-xs font-mono uppercase tracking-widest ${isOpeningSoon ? "text-violet-400 font-bold" : "text-slate-500"}`}>
          {isOpeningSoon ? "Opening soon" : "Next session"}
        </span>
      </div>

      {/* Main message */}
      <p className="text-white font-bold text-base sm:text-lg mb-1 font-[Outfit]">
        Rooms open {when} at 7pm UK time
      </p>
      <p className="text-slate-500 text-xs mb-3">
        Every evening · 7pm–11pm · GMT / BST
      </p>

      {/* Live countdown */}
      <div className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl ${
        isOpeningSoon
          ? "bg-violet-500/15 border border-violet-500/30"
          : "bg-[#141322] border border-purple-500/20"
      }`}>
        <span className={`text-[10px] font-mono uppercase tracking-widest ${isOpeningSoon ? "text-violet-400" : "text-slate-500"}`}>
          Opens in
        </span>
        <span className={`font-mono text-2xl font-black tracking-tight ${isOpeningSoon ? "gradient-text" : "text-white"}`}>
          {countdown}
        </span>
      </div>

      {isOpeningSoon && (
        <p className="text-violet-300 text-xs mt-2.5 font-medium">
          Almost time — get ready to join!
        </p>
      )}
    </motion.div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ online_count: 0, active_rooms: 0 });

  useEffect(() => {
    axios.get(`${API}/public/stats`).then(r => setStats(r.data)).catch(() => {});
    const interval = setInterval(() => {
      axios.get(`${API}/public/stats`).then(r => setStats(r.data)).catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#07070B] overflow-hidden relative">
      {/* Background grid */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.12)_0%,transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(6,182,212,0.08)_0%,transparent_60%)] pointer-events-none" />

      {/* Header */}
      <nav className="relative z-10 flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
        <div className="flex items-center">
          <img src={LOGO_URL} alt="Let's Check Who's On?" className="h-12 w-auto object-contain drop-shadow-[0_0_10px_rgba(139,92,246,0.6)]" />
        </div>
        <div className="flex gap-3">
          <button
            data-testid="nav-login-btn"
            onClick={() => navigate("/login")}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
          >
            Sign In
          </button>
          <button
            data-testid="nav-register-btn"
            onClick={() => navigate("/register")}
            className="px-4 py-2 text-sm bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-full font-semibold hover:opacity-90 transition-opacity"
          >
            Join Free
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 flex flex-col items-center text-center px-4 pt-10 pb-20 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col items-center"
        >
          {/* Big logo */}
          <img
            src={LOGO_URL}
            alt="Let's Check Who's On?"
            className="w-64 sm:w-80 h-auto object-contain mb-8 drop-shadow-[0_0_30px_rgba(139,92,246,0.5)]"
          />

          <p className="text-lg sm:text-xl text-slate-400 max-w-xl mx-auto leading-relaxed mb-3">
            See who's online. Find a room. Start talking.
          </p>
          <p className="text-sm text-slate-500 mb-10">
            Real-time public chatrooms for adults. No algorithms, no feeds — just conversation.
          </p>
        </motion.div>

        {/* Live stats pill */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="flex items-center gap-6 bg-[#141322] border border-purple-500/20 rounded-full px-6 py-3 mb-6"
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 pulse-online" />
            <span className="text-sm font-semibold text-white">{stats.online_count}</span>
            <span className="text-sm text-slate-400">online now</span>
          </div>
          <div className="w-px h-4 bg-slate-700" />
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-semibold text-white">{stats.active_rooms}</span>
            <span className="text-sm text-slate-400">rooms active</span>
          </div>
        </motion.div>

        {/* ── Live schedule banner ── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.38 }}
          className="w-full max-w-md"
        >
          <ScheduleBanner />
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4 w-full max-w-sm"
        >
          <button
            data-testid="cta-lets-check-btn"
            onClick={() => navigate("/register")}
            className="flex-1 py-4 bg-gradient-to-r from-violet-600 via-purple-500 to-cyan-500 text-white font-bold text-base rounded-xl hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2 glow-purple"
          >
            LET'S CHECK <ArrowRight className="w-5 h-5" />
          </button>
          <button
            data-testid="cta-see-whos-on-btn"
            onClick={() => navigate("/login")}
            className="flex-1 py-4 bg-[#141322] border border-purple-500/30 text-white font-semibold text-base rounded-xl hover:border-cyan-400/50 hover:bg-[#1C1936] transition-all"
          >
            SEE WHO'S ON
          </button>
        </motion.div>
      </section>

      {/* Avatar showcase */}
      <section className="relative z-10 max-w-4xl mx-auto px-4 pb-20">
        <div className="flex justify-center gap-3 flex-wrap">
          {AVATARS.map((a, i) => (
            <motion.div
              key={a.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + i * 0.08 }}
              className="flex flex-col items-center gap-2"
            >
              <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${a.bg} overflow-hidden ring-2 ring-white/10 shadow-lg`}>
                <img
                  src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(a.seed)}&backgroundColor=transparent`}
                  alt={a.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <span className="text-xs text-slate-500">{a.name}</span>
            </motion.div>
          ))}
        </div>
        <p className="text-center text-sm text-slate-500 mt-4">
          Choose your character. No real names. No profile photos.
        </p>
      </section>

      {/* Features */}
      <section className="relative z-10 max-w-5xl mx-auto px-4 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: <Users className="w-6 h-6 text-purple-400" />, title: "See Who's Online", desc: "Know who's around before you even join a room." },
            { icon: <MessageSquare className="w-6 h-6 text-cyan-400" />, title: "Public Chatrooms", desc: "Drop into any room. Talk to real people, right now." },
            { icon: <Zap className="w-6 h-6 text-amber-400" />, title: "Real-time & Live", desc: "Messages appear instantly. No refresh. Just conversation." },
          ].map((f) => (
            <div key={f.title} className="bg-[#0E0E17] border border-purple-500/15 rounded-xl p-6 hover:border-purple-500/30 transition-colors">
              <div className="mb-3">{f.icon}</div>
              <h3 className="font-bold text-white mb-1 font-[Outfit]">{f.title}</h3>
              <p className="text-sm text-slate-400">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ SOCIALISING, NOT SOCIAL MEDIA ═══════════════════════════════════ */}
      <section className="relative z-10 w-full py-24 md:py-32 overflow-hidden">
        {/* Section edge lines */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
        {/* Subtle radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_40%,rgba(139,92,246,0.07)_0%,transparent_65%)] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6">

          {/* ── Main heading ── */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16 md:mb-20"
          >
            <h2 className="font-[Outfit] font-black leading-none tracking-tight">
              <span className="block text-5xl sm:text-7xl lg:text-8xl gradient-text">
                SOCIALISING,
              </span>
              <span className="block text-xl sm:text-3xl text-slate-500 font-light tracking-[0.35em] uppercase my-3 md:my-4">
                not
              </span>
              <span className="block text-4xl sm:text-6xl lg:text-7xl text-slate-500 line-through decoration-purple-500/50 decoration-2 underline-offset-8">
                SOCIAL MEDIA.
              </span>
            </h2>
          </motion.div>

          {/* ── Body + comparison ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start mb-16 md:mb-20">

            {/* Left: copy */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="space-y-6"
            >
              <p className="text-slate-300 text-lg sm:text-xl leading-relaxed">
                Social media is full of posts, likes, followers and endless scrolling.
              </p>
              <p className="text-white text-xl sm:text-2xl font-semibold font-[Outfit] leading-snug">
                Who's On? is different.<br />
                It's about actually being social.
              </p>
              <p className="text-slate-200 text-lg sm:text-xl leading-relaxed">
                Drop in. See who's around.<br />
                Pick a room. Have a chat.
              </p>

              {/* The "No X" lines */}
              <div className="space-y-2.5 py-2">
                {[
                  "No likes.",
                  "No follower counts.",
                  "No endless feed.",
                  "No pressure to post.",
                ].map(line => (
                  <p key={line} className="flex items-center gap-3 text-slate-400 text-base">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                    {line}
                  </p>
                ))}
              </div>

              <p className="text-white text-xl font-medium leading-relaxed">
                Just people, talking to people.
              </p>
              <p className="text-slate-400 text-base leading-relaxed">
                Because sometimes you don't want another social network.
                You just want somewhere to socialise.
              </p>
            </motion.div>

            {/* Right: comparison */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="grid grid-cols-2 gap-3 sm:gap-4"
            >
              {/* Social Media — muted */}
              <div className="bg-[#0E0E17] rounded-2xl p-5 sm:p-6 border border-slate-800/60">
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-600 mb-5">
                  Social Media
                </p>
                <div className="space-y-4">
                  {["Posts", "Likes", "Followers", "Scrolling"].map(item => (
                    <p key={item}
                      className="text-slate-600 text-sm sm:text-base line-through decoration-slate-700">
                      {item}
                    </p>
                  ))}
                </div>
              </div>

              {/* Who's On — alive */}
              <div className="bg-[#0E0E17] rounded-2xl p-5 sm:p-6 border border-purple-500/25 relative">
                {/* Top glow accent */}
                <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl bg-gradient-to-r from-violet-500 to-cyan-400" />
                <p className="text-[10px] font-mono uppercase tracking-widest text-purple-400 mb-5">
                  WHO'S ON?
                </p>
                <div className="space-y-4">
                  {["Rooms", "Conversation", "People", "Socialising"].map((item, i) => (
                    <p key={item}
                      className={`text-sm sm:text-base font-semibold ${
                        i === 3 ? "gradient-text font-[Outfit]" : "text-white"
                      }`}>
                      {item}
                    </p>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* ── CTA ── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col items-center gap-3"
          >
            <button
              data-testid="see-whos-on-section-btn"
              onClick={() => navigate("/login")}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-violet-600 via-purple-500 to-cyan-500 text-white font-bold text-base rounded-xl hover:opacity-90 active:scale-95 transition-all glow-purple"
            >
              SEE WHO'S ON <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-xs text-slate-600">18+ · Free to join · No profile photos required</p>
          </motion.div>

        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-purple-500/10 mt-8">
        <div className="max-w-5xl mx-auto px-4 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col items-center sm:items-start gap-1">
            <span className="text-xs text-slate-600">checkwhoson.com &nbsp;·&nbsp; 18+ &nbsp;·&nbsp; Public chatrooms for adults</span>
            <span className="text-xs text-slate-700">Evening sessions only, 7–11pm UK time. Messages deleted nightly.</span>
          </div>
          <nav className="flex flex-wrap justify-center sm:justify-end gap-x-5 gap-y-2">
            {[
              { href: "/terms",      label: "Terms & Conditions" },
              { href: "/privacy",    label: "Privacy Policy"     },
              { href: "/guidelines", label: "Community Guidelines" },
            ].map(({ href, label }) => (
              <a key={href} href={href}
                className="text-xs text-slate-600 hover:text-purple-400 transition-colors underline-offset-2 hover:underline">
                {label}
              </a>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}
